import Dialog from '@material-ui/core/Dialog'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from '@material-ui/icons/Close'
import classnames from 'classnames'
import * as mixpanel from 'mixpanel-browser'
import * as React from 'react'
import { CSSTransition, SwitchTransition } from 'react-transition-group'

import { stepsData } from 'data/parentalInstructions'
import { InstructionsNav } from './InstructionsNav'

import SnackbarService from '../../services/snackbars'

import './style.scss'

const renderActiveStep = activeStepData => (
  <div className="active-step-wrap">
    <div className={classnames('active-step-text')}>
      {activeStepData.number ? (
        <span className="active-step-number">{activeStepData.number}</span>
      ) : (
        <span className="active-step-line" />
      )}
      <div
        className={classnames([
          activeStepData?.bg && 'active-step-labels-reverse'
        ])}
      >
        <p className="active-step-label">{activeStepData.label}</p>
        <p className="active-step-sub-label">{activeStepData?.subLabel}</p>
      </div>
    </div>
  </div>
)

interface IProps {
  updateParentInstructComplete: (
    arg1: () => void,
    arg2: (e: string) => void
  ) => void
  userId: string
}

export const ParentalInstructions = React.forwardRef<any, IProps>(
  ({ updateParentInstructComplete, userId }, ref) => {
    const [open, setOpen] = React.useState<boolean>(false)
    const [activeStep, setActiveStep] = React.useState<number>(0)
    const [loading, setLoading] = React.useState<boolean>(false)
    const [animUp, setAnimUp] = React.useState<boolean>(false)
    const timer = React.useRef<any>()

    React.useImperativeHandle(ref, () => ({
      handleOpenDialog() {
        setOpen(true)
      }
    }))

    const onClickNavItem = React.useCallback(
      (id: number): void => {
        if (id < activeStep) {
          setAnimUp(true)
        }
        setActiveStep(id)
      },
      [activeStep]
    )

    const onClickNext = React.useCallback((): void => {
      setAnimUp(false)
      setActiveStep(prev => ++prev)
    }, [])

    const handleClose = React.useCallback(() => {
      setOpen(false)
      timer.current = setTimeout(() => {
        setActiveStep(0)
      }, 500)
    }, [setOpen])

    React.useEffect(
      () => () => {
        clearTimeout(timer.current)
      },
      []
    )

    const activeStepData = React.useMemo(
      () => stepsData.find(({ id }) => id === activeStep) ?? null,
      [activeStep]
    )

    const isActiveStepLast = React.useMemo(
      () => activeStep === stepsData.length - 1,
      [activeStep]
    )

    const onClickDone = React.useCallback((): void => {
      setLoading(true)
      updateParentInstructComplete(
        () => {
          setLoading(false)
          handleClose()

          mixpanel.identify(userId)
          mixpanel.track('Completed parental instructions')
        },
        e => SnackbarService.showSnackbar(e, 'error')
      )
    }, [])

    const onBackButtonEvent = event => {
      event.preventDefault()

      handleClose()

      window.removeEventListener('popstate', onBackButtonEvent)
    }

    return (
      <div ref={ref}>
        <Dialog
          classes={{
            root: 'instructions',
            paper: classnames('dialog-paper', [
              activeStepData?.bg,
              activeStepData?.bg && 'with-col-bg',
              (activeStepData?.number === 2 || activeStepData?.number === 3) &&
                'no-animated',
              animUp && 'animate-up'
            ])
          }}
          fullScreen={true}
          open={open}
          onEnter={() => {
            window.history.pushState(null, '', window.location.pathname)
            window.addEventListener('popstate', onBackButtonEvent)
          }}
          onClose={() => {
            window.removeEventListener('popstate', onBackButtonEvent)
            window.history.back()

            handleClose()
          }}
        >
          <div className="dialog-content">
            <IconButton
              classes={{ root: 'btn-close' }}
              color="inherit"
              onClick={() => {
                window.removeEventListener('popstate', onBackButtonEvent)
                window.history.back()

                handleClose()
              }}
              aria-label="close"
            >
              <CloseIcon classes={{ root: 'btn-close-icon' }} />
            </IconButton>

            <SwitchTransition mode="out-in">
              <CSSTransition
                key={activeStep}
                timeout={5000}
                addEndListener={(node, done) => {
                  node.addEventListener('transitionend', done, false)
                }}
                classNames="fade"
              >
                {activeStepData && renderActiveStep(activeStepData)}
              </CSSTransition>
            </SwitchTransition>

            <InstructionsNav
              activeStep={activeStep}
              activeStepHasNav={!!activeStepData?.hasNavigation}
              isActiveStepLast={isActiveStepLast}
              onClickNavItem={onClickNavItem}
              onClickNext={onClickNext}
              onClickDone={onClickDone}
              btnLoading={loading}
            />
          </div>
        </Dialog>
      </div>
    )
  }
)



// WEBPACK FOOTER //
// ./src/components/ParentalInstructions/index.tsx